import { createElement, type ReactNode, useId } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";

export type MarkdownComponents = Pick<Components, "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "img">;

export function Markdown({ children, components }: { children: string; components?: MarkdownComponents }): ReactNode {
  const id = useId();
  const footnotePrefix = `markdown-${id}-`;
  const footnoteLabelId = `${footnotePrefix}footnote-label`;
  const Heading = components?.h2;

  return (
    <div className="markdown">
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        remarkRehypeOptions={{ clobberPrefix: footnotePrefix }}
        components={{
          ...components,
          // The converter namespaces footnote references, but uses a fixed heading ID.
          h2: ({ id: headingId, node, ...props }) => {
            const headingProps = { ...props, id: headingId === "footnote-label" ? footnoteLabelId : headingId };
            return Heading ? createElement(Heading, { ...headingProps, node }) : <h2 {...headingProps} />;
          },
          a: ({ "aria-describedby": describedBy, children: linkChildren, href, node: _, ...props }) => (
            <a
              {...props}
              aria-describedby={describedBy === "footnote-label" ? footnoteLabelId : describedBy}
              href={href}
              target={href?.startsWith("#") ? undefined : "_blank"}
              rel={href?.startsWith("#") ? undefined : "noreferrer noopener"}
            >
              {linkChildren}
            </a>
          ),
          table: ({ children: tableChildren, node: _, ...props }) => (
            // biome-ignore lint/a11y/noNoninteractiveTabindex: Scroll regions need focus so keyboard users can reach overflowing columns.
            <section className="markdown-table-wrap" aria-label="Scrollable table" tabIndex={0}>
              <table {...props}>{tableChildren}</table>
            </section>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
